import { motion } from "framer-motion";
import { CalendarX, Inbox, UserX } from "lucide-react";
import { ChapterHeading, Reveal } from "@/components/Reveal";
import { scrollToId } from "@/lib/scroll";

const PAINS = [
  {
    icon: CalendarX,
    title: "Faltas sem aviso",
    text: "O cliente marca e não aparece. Sem confirmação automática, a cadeira fica vazia e o prejuízo fica com você.",
    loss: "R$ 480 /semana*",
    lossLabel: "em horários vagos (estimativa ilustrativa)",
  },
  {
    icon: Inbox,
    title: "Recepção sobrecarregada",
    text: "Sua equipe atende, agenda, confirma e ainda responde o WhatsApp. Em algum lugar, algo sempre fica para trás.",
    loss: "3h /dia",
    lossLabel: "perdidas em tarefas repetitivas",
  },
  {
    icon: UserX,
    title: "Cliente que desaparece",
    text: "Mensagem enviada às 21h, resposta às 9h do dia seguinte. Nesse intervalo, ele já fechou com o concorrente.",
    loss: "1 em 3*",
    lossLabel: "leads perdidos por demora (estimativa ilustrativa)",
  },
];

export default function PainSection() {
  return (
    <section id="dor" data-testid="pain-section" className="relative overflow-hidden py-28 sm:py-36">
      <div className="mx-auto max-w-7xl px-5 lg:px-10">
        <ChapterHeading
          index="01"
          eyebrow="O custo invisível"
          lines={[
            "Enquanto você demora para responder,",
            "seu cliente pode estar falando",
            "com outra empresa.",
          ]}
        />

        <div className="mt-16 grid gap-5 md:grid-cols-3">
          {PAINS.map((p, i) => (
            <motion.div
              key={p.title}
              data-testid={`pain-card-${i + 1}`}
              initial={{ opacity: 0, y: 44 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -6 }}
              className="group relative overflow-hidden rounded-3xl border border-[var(--line)] bg-[var(--card)] p-8 transition-[box-shadow,border-color] duration-300 hover:border-[var(--danger)]/40 hover:shadow-[0_24px_50px_-20px_rgba(214,79,79,0.25)]"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[var(--danger)]/10 text-[var(--danger)]">
                <p.icon className="h-6 w-6" />
              </div>
              <h3 className="mt-6 font-display text-xl font-bold text-[var(--ink)]">{p.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-[var(--muted)]">{p.text}</p>
              <div className="mt-8 flex items-end justify-between border-t border-[var(--line)] pt-5">
                <div>
                  <p className="font-display text-2xl font-extrabold text-[var(--danger)]">{p.loss}</p>
                  <p className="text-xs text-[var(--muted)]">{p.lossLabel}</p>
                </div>
                <span className="font-mono2 text-[10px] uppercase tracking-widest text-[var(--muted)] opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                  dor real
                </span>
              </div>
            </motion.div>
          ))}
        </div>

        <Reveal delay={0.15}>
          <p className="mt-6 text-xs text-[var(--muted)]">
            * Valores e proporções acima são estimativas ilustrativas para exemplificar o problema — não representam dados medidos de clientes.
          </p>
        </Reveal>

        <Reveal delay={0.2} className="mt-8">
          <div className="flex flex-col items-start gap-6 rounded-3xl border border-[var(--line)] bg-sage/25 p-8 dark:bg-petroldeep/60 sm:flex-row sm:items-center sm:justify-between">
            <p className="max-w-xl font-display text-lg font-semibold text-[var(--ink)] sm:text-xl">
              Cada minuto sem resposta é receita saindo pela porta. A boa notícia: isso tem solução.
            </p>
            <button
              data-testid="pain-cta-solution"
              onClick={() => scrollToId("solucao")}
              className="shrink-0 rounded-full bg-petrol px-6 py-3.5 text-sm font-semibold text-white transition-[transform,box-shadow] duration-300 hover:scale-[1.03] hover:shadow-[0_10px_30px_rgba(18,60,74,0.35)] dark:bg-turquoise dark:text-petrol"
            >
              Ver a solução
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
